import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useIsMutating } from "@tanstack/react-query";

import { applyPendingBranding, checkForBrandingUpdate, useBrandingStore } from "./index";
import { isUserBusy, markWorkFinished, startActivityTracking } from "./activity";

/** How often an open, visible tab asks whether the branding changed. Kept at
 * a minute: every tab in an office shares one IP against the API's per-IP
 * rate limit. Returning to the tab checks straight away. */
export const BRANDING_POLL_MS = 60_000;
/** How often a queued change re-checks whether the user is free yet. */
const BUSY_RECHECK_MS = 2_000;

/**
 * Picks up white-labeling changes a Super Admin saves while this page is
 * open. Applied in place (title, favicon, colours, logo, name) - never a page
 * reload - as soon as the user isn't mid-task; until then a small notice says
 * it is coming, with "Apply now". Mounted once, inside the router.
 */
export function BrandingUpdateWatcher() {
  const pending = useBrandingStore((s) => s.pending);
  const mutating = useIsMutating();
  const { pathname } = useLocation();
  const [waiting, setWaiting] = useState(false);

  useEffect(() => startActivityTracking(), []);

  useEffect(() => {
    const check = () => {
      if (document.visibilityState === "visible") void checkForBrandingUpdate();
    };
    const id = window.setInterval(check, BRANDING_POLL_MS);
    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", check);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("focus", check);
    };
  }, []);

  // Leaving a page, or a save finishing, ends the work that was in progress.
  useEffect(() => markWorkFinished(), [pathname]);
  const wasMutating = useRef(mutating);
  useEffect(() => {
    if (wasMutating.current > 0 && mutating === 0) markWorkFinished();
    wasMutating.current = mutating;
  }, [mutating]);

  useEffect(() => {
    if (!pending) {
      setWaiting(false);
      return;
    }
    const tryApply = () => {
      if (mutating === 0 && !isUserBusy()) {
        applyPendingBranding();
        return true;
      }
      setWaiting(true);
      return false;
    };
    if (tryApply()) return;
    const id = window.setInterval(tryApply, BUSY_RECHECK_MS);
    return () => window.clearInterval(id);
  }, [pending, mutating, pathname]);

  if (!pending || !waiting) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 right-4 z-50 w-[min(22rem,calc(100vw-2rem))] rounded-xl2 border border-slate-200 bg-white p-4 shadow-soft"
    >
      <p className="text-sm font-semibold text-slate-900">Your organisation's branding was updated</p>
      <p className="mt-1 text-sm text-slate-600">
        It will apply as soon as you finish what you are doing here. Nothing you have typed is lost.
      </p>
      <button
        type="button"
        onClick={applyPendingBranding}
        className="mt-3 rounded text-sm font-medium text-primary-700 underline-offset-2 hover:text-primary-800 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        Apply now
      </button>
    </div>
  );
}
